import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Copy,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Link2,
  Lock,
  RotateCcw,
  Trash2,
  Type,
} from 'lucide-react';
import {
  MAX_SECTION_NAME_LENGTH,
  duplicateSection,
  hideSection,
  mergeSections,
  moveSection,
  removeSection,
  renameSection,
  resetSlot,
  setSlot,
  showSection,
  splitSection,
  type SectionSlotValueInput,
  type SectionSlotView,
  type SectionView,
} from '@nopain-newsletter/core/sections';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { ImageSourceInput } from '../ImageSourceInput';
import { cn } from '../../lib/utils';
import { rowSections, slotLabel } from './sectionText';
import type { SectionEditor, SectionOperation } from './useSectionEditor';

type EditorApi = Pick<SectionEditor, 'apply' | 'tryOp' | 'view' | 'revision'>;

const SLOT_ICON = { text: Type, image: ImageIcon, link: Link2, button: Link2 } as const;

function Note({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex gap-2 rounded-xl border border-border bg-bg px-3 py-2.5 text-[13px] text-muted">
      <span className="mt-0.5 flex-shrink-0" aria-hidden="true">
        {icon}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function Problems({ slot }: { slot: SectionSlotView }) {
  if (slot.problems.length === 0) return null;
  return (
    <ul className="m-0 flex list-none flex-col gap-1 rounded-lg bg-warning-bg px-3 py-2 text-[13px] text-warning-text">
      {slot.problems.map((problem) => (
        <li key={`${problem.code}:${problem.message}`}>
          <span aria-hidden="true">! </span>
          {problem.message}
        </li>
      ))}
    </ul>
  );
}

/**
 * The fields of one slot. What is typed stays in the field as typed; every
 * change is offered to the core at once, and its refusal is shown under
 * the field while the mail keeps the last value it accepted.
 */
function SlotFields({
  slot,
  editor,
  readOnly,
  onReset,
}: {
  slot: SectionSlotView;
  editor: EditorApi;
  readOnly: boolean;
  onReset: () => void;
}) {
  const [text, setText] = useState(slot.values.text ?? '');
  const [src, setSrc] = useState(slot.values.src ?? '');
  const [alt, setAlt] = useState(slot.values.alt ?? '');
  const [href, setHref] = useState(slot.values.href ?? '');
  const [error, setError] = useState<string | null>(null);
  const id = `slot-${slot.id}`;

  const offer = (input: SectionSlotValueInput, coalesce?: string) => {
    const result = editor.apply(
      (doc, model) => setSlot(doc, model, slot.id, input),
      coalesce ?? `slot:${slot.id}`,
    );
    setError(result.ok ? null : result.error.message);
  };

  const refusal = useMemo(() => {
    if (slot.protected === null) return null;
    const probe: SectionSlotValueInput =
      slot.kind === 'text' ? { text: '' } : slot.kind === 'image' ? { alt: '' } : { href: '' };
    const result = editor.tryOp((doc, model) => setSlot(doc, model, slot.id, probe));
    return result.ok ? null : result.error.message;
  }, [editor, slot.id, slot.kind, slot.protected]);

  if (slot.protected !== null) {
    return (
      <div className="flex flex-col gap-2">
        <p className="m-0 break-words rounded-lg border border-border bg-bg px-3 py-2 text-sm">
          {slot.kind === 'text' ? slot.values.text : slot.values.href}
        </p>
        <Note icon={<Lock size={14} />}>
          {refusal ?? 'This value is protected and cannot be changed.'}
        </Note>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {slot.kind === 'text' ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={id}>{slot.hidden ? 'Hidden text (preheader)' : 'Text'}</Label>
          <textarea
            id={id}
            value={text}
            disabled={readOnly}
            rows={Math.min(8, Math.max(2, Math.ceil(text.length / 38)))}
            aria-invalid={error !== null}
            aria-describedby={error !== null ? `${id}-error` : undefined}
            className="w-full resize-y rounded-[9px] border border-border bg-white px-3 py-2 text-sm"
            onChange={(event) => {
              setText(event.target.value);
              offer({ text: event.target.value });
            }}
          />
          {slot.mergeFields.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
              <span>Merge fields, keep them as written:</span>
              {slot.mergeFields.map((field, i) => (
                <code
                  key={`${field}:${i}`}
                  className="rounded-md bg-accent-soft px-1.5 py-0.5 font-mono text-[11px] text-link"
                >
                  {field}
                </code>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {slot.kind === 'image' ? (
        <>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={id}>Image address</Label>
            <ImageSourceInput
              id={id}
              label="Image address"
              variant="card"
              value={src}
              onChange={(next) => {
                if (readOnly) return;
                setSrc(next);
                offer({ src: next }, `src:${slot.id}`);
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-alt`}>Description (alt text)</Label>
            <Input
              id={`${id}-alt`}
              value={alt}
              disabled={readOnly}
              onChange={(event) => {
                setAlt(event.target.value);
                offer({ alt: event.target.value }, `alt:${slot.id}`);
              }}
            />
          </div>
          {slot.hasOutlookTwin ? (
            slot.values.outlookSynced ? (
              <p className="m-0 text-xs text-muted">
                The Outlook version of this picture is written with the same image.
              </p>
            ) : (
              <div className="flex flex-col gap-1.5 text-xs text-muted">
                <span>
                  This picture has a separate Outlook version. It follows when you change the image.
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  className="self-start"
                  disabled={readOnly}
                  onClick={() => offer({ syncOutlook: true }, `sync:${slot.id}`)}
                >
                  Use this picture in Outlook too
                </Button>
              </div>
            )
          ) : null}
        </>
      ) : null}

      {slot.kind === 'link' || slot.kind === 'button' ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={id}>{slot.kind === 'button' ? 'Button link' : 'Link'}</Label>
          <Input
            id={id}
            value={href}
            disabled={readOnly}
            aria-invalid={error !== null}
            onChange={(event) => {
              setHref(event.target.value);
              offer({ href: event.target.value });
            }}
          />
        </div>
      ) : null}

      {error !== null ? (
        <Alert className="px-3 py-2 text-[13px]">
          <span id={`${id}-error`}>{error}</span> The mail keeps the last value that was accepted.
        </Alert>
      ) : null}
      <Problems slot={slot} />
      <Button
        variant="ghost"
        size="sm"
        className="self-end"
        disabled={readOnly || !slot.edited}
        onClick={onReset}
      >
        <RotateCcw size={14} aria-hidden="true" />
        Reset to original
      </Button>
    </div>
  );
}

function SlotRow({
  slot,
  open,
  editor,
  readOnly,
  onToggle,
}: {
  slot: SectionSlotView;
  open: boolean;
  editor: EditorApi;
  readOnly: boolean;
  onToggle: () => void;
}) {
  const [resets, setResets] = useState(0);
  const Icon = SLOT_ICON[slot.kind];
  const label = slotLabel(slot);
  return (
    <li className={cn('border-b border-border last:border-b-0', open ? 'bg-hover' : '')}>
      <button
        type="button"
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left"
        onClick={onToggle}
      >
        <Icon size={16} aria-hidden="true" className="flex-shrink-0 text-muted" />
        <span className="min-w-0 flex-grow">
          <span className="block text-xs text-muted">
            {label.kind}
            {slot.protected !== null ? ' · protected' : ''}
            {slot.edited ? ' · changed' : ''}
          </span>
          <span className="block truncate text-sm">{label.value}</span>
        </span>
        {slot.protected !== null ? (
          <Lock size={13} aria-hidden="true" className="flex-shrink-0 text-faint" />
        ) : null}
        {slot.problems.length > 0 ? (
          <span
            className="h-2 w-2 flex-shrink-0 rounded-full bg-warning-marker"
            role="img"
            aria-label={`${slot.problems.length} ${slot.problems.length === 1 ? 'problem' : 'problems'}`}
          />
        ) : null}
      </button>
      {open ? (
        <div className="px-3 pb-3">
          <SlotFields
            key={`${editor.revision}:${resets}`}
            slot={slot}
            editor={editor}
            readOnly={readOnly}
            onReset={() => {
              editor.apply((doc, model) => resetSlot(doc, model, slot.id));
              setResets((n) => n + 1);
            }}
          />
        </div>
      ) : null}
    </li>
  );
}

/**
 * Everything about the selected section: its name, why it is cut or
 * locked the way it is, the layout operations, and one row per text,
 * image and link that can be changed. Every button asks the core; a
 * refusal is shown with the core's own words.
 */
export function SectionInspector({
  section,
  editor,
  selectedSlot,
  readOnly,
  onSelectSlot,
  onSelectSection,
}: {
  section: SectionView;
  editor: EditorApi;
  selectedSlot: string | null;
  readOnly: boolean;
  onSelectSlot: (slotId: string | null) => void;
  onSelectSection: (sectionId: string | null) => void;
}) {
  const [notice, setNotice] = useState<string | null>(null);
  const [name, setName] = useState(section.name);
  useEffect(() => {
    setName(section.name);
    // The name is read again when another section is shown or the model was replaced.
  }, [section.id, section.name, editor.revision]);
  useEffect(() => setNotice(null), [section.id]);

  const ordered = rowSections(editor.view.sections);
  const position = ordered.findIndex((s) => s.id === section.id);
  const above = position > 0 ? ordered[position - 1] : undefined;
  const below = position >= 0 ? ordered[position + 1] : undefined;
  const inOrder = section.rows !== null;
  const movable = section.kind === 'movable';

  const run = (operation: SectionOperation, after?: (created: string | undefined) => void) => {
    const result = editor.apply(operation);
    if (result.ok) {
      setNotice(null);
      after?.(result.created);
    } else {
      setNotice(result.error.message);
    }
  };

  const commitName = () => {
    if (name === section.name) return;
    run((doc, model) => renameSection(doc, model, section.sectionId, name));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="section-name">Section name</Label>
        {inOrder ? (
          <Input
            id="section-name"
            value={name}
            disabled={readOnly}
            maxLength={MAX_SECTION_NAME_LENGTH * 2}
            onChange={(event) => setName(event.target.value)}
            onBlur={commitName}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commitName();
            }}
          />
        ) : (
          <p id="section-name" className="m-0 text-[15px] font-semibold">
            {section.name}
          </p>
        )}
      </div>

      {!movable ? (
        <Note icon={<Lock size={14} />}>
          <strong className="font-semibold text-text">Locked.</strong> {section.reason}
        </Note>
      ) : section.confidence !== 'high' ? (
        <Note icon="?">
          <strong className="font-semibold text-text">This cut is a proposal.</strong>{' '}
          {section.reason} Merge or split below if it is not how you see the mail.
        </Note>
      ) : null}

      {inOrder && movable ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              aria-label="Move up"
              disabled={readOnly || position <= 0}
              onClick={() => run((doc, model) => moveSection(doc, model, section.id, position - 1))}
            >
              <ArrowUp size={15} aria-hidden="true" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              aria-label="Move down"
              disabled={readOnly || position >= ordered.length - 1}
              onClick={() => run((doc, model) => moveSection(doc, model, section.id, position + 1))}
            >
              <ArrowDown size={15} aria-hidden="true" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={readOnly}
              onClick={() =>
                run(
                  (doc, model) => duplicateSection(doc, model, section.id),
                  (created) => {
                    if (created !== undefined) onSelectSection(created);
                  },
                )
              }
            >
              <Copy size={15} aria-hidden="true" />
              Duplicate
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={readOnly}
              onClick={() =>
                run((doc, model) =>
                  section.hidden
                    ? showSection(doc, model, section.id)
                    : hideSection(doc, model, section.id),
                )
              }
            >
              {section.hidden ? (
                <Eye size={15} aria-hidden="true" />
              ) : (
                <EyeOff size={15} aria-hidden="true" />
              )}
              {section.hidden ? 'Show' : 'Hide'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="text-danger-text"
              disabled={readOnly}
              onClick={() =>
                run(
                  (doc, model) => removeSection(doc, model, section.id),
                  () => onSelectSection(null),
                )
              }
            >
              <Trash2 size={15} aria-hidden="true" />
              Remove
            </Button>
          </div>
          {section.hidden ? (
            <p className="m-0 text-xs text-muted">
              Hidden: this section is left out of the mail. Its changes are kept.
            </p>
          ) : null}
        </div>
      ) : null}

      {inOrder ? (
        <details className="rounded-xl border border-border px-3 py-2 text-sm">
          <summary className="cursor-pointer font-medium">Change the cut</summary>
          <div className="mt-2 flex flex-col items-start gap-1.5">
            {above ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={readOnly}
                onClick={() =>
                  run(
                    (doc, model) => mergeSections(doc, model, above.sectionId, section.sectionId),
                    () => onSelectSection(above.id),
                  )
                }
              >
                Merge with “{above.name}” above
              </Button>
            ) : null}
            {below ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={readOnly}
                onClick={() =>
                  run((doc, model) => mergeSections(doc, model, section.sectionId, below.sectionId))
                }
              >
                Merge with “{below.name}” below
              </Button>
            ) : null}
            {section.splitPoints.map((point) => (
              <Button
                key={point.blockId}
                variant="secondary"
                size="sm"
                disabled={readOnly}
                onClick={() =>
                  run((doc, model) => splitSection(doc, model, section.sectionId, point.blockId))
                }
              >
                Split before “{point.name}”
              </Button>
            ))}
            {section.splitPoints.length === 0 ? (
              <p className="m-0 text-xs text-muted">
                This section has no row where it can be split.
              </p>
            ) : null}
          </div>
        </details>
      ) : null}

      {notice !== null ? <Alert>{notice}</Alert> : null}

      <div>
        <h3 className="m-0 mb-1.5 text-[13px] font-medium text-muted">What you can change here</h3>
        {section.slots.length === 0 ? (
          <p className="m-0 text-sm text-muted">
            This section has no text, image or link that can be changed.
          </p>
        ) : (
          <ul className="m-0 list-none overflow-hidden rounded-xl border border-border bg-card p-0">
            {section.slots.map((slot) => (
              <SlotRow
                key={slot.id}
                slot={slot}
                open={slot.id === selectedSlot}
                editor={editor}
                readOnly={readOnly}
                onToggle={() => onSelectSlot(slot.id === selectedSlot ? null : slot.id)}
              />
            ))}
          </ul>
        )}
        <p className="m-0 mt-2 text-xs text-muted">
          Colours, fonts and spacing belong to the design of the mail and stay as they are. The HTML
          source editor changes everything else.
        </p>
      </div>
    </div>
  );
}
